import { type FormEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedDate } from 'react-intl';

import { useDetectionGroups, useIntl } from '../../hooks';
import { formatDuration } from '../../lib/format-duration';
import { hzToMhz } from '../../lib/frequency';
import { formatOptionalNumber, parseKvNumber } from '../../lib/number-format';
import { getStatusTranslationKey, getStatusVariant } from '../../services/detection-status';
import { type DetectionGroup, DetectionStatus, SignalType } from '../../types';
import { renderSignalDirectionIcon } from '../SignalDirectionIcon.js';
import { renderSignalTypeIcon } from '../SignalTypeIcon';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui';
import { SuppressDetectionGroupAction } from './SuppressDetectionGroupAction';

const DEFAULT_PAGE_SIZE = 5;
const COL_SPAN = 12;
export const HIGH_PRIORITY_SUPPRESSION = 100;

type LinkedDetectionGroupRowProps = {
  canManageSuppressions: boolean;
  defaultDurationSec: number;
  group: DetectionGroup;
  isLoading: boolean;
  maxSuppressionTime: number;
  onSuppressGroup: (group: DetectionGroup, durationSec: number, priority?: number) => void;
};

const LinkedDetectionGroupRow = memo(function LinkedDetectionGroupRow({
  canManageSuppressions,
  defaultDurationSec,
  group,
  isLoading,
  maxSuppressionTime,
  onSuppressGroup,
}: LinkedDetectionGroupRowProps) {
  const intl = useIntl();
  const [durationValue, setDurationValue] = useState<string>(String(defaultDurationSec));
  const [isPriority, setIsPriority] = useState(false);
  // The row mounts as soon as the linked device is selected, which can race the primary
  // device's settings fetch that `defaultDurationSec` comes from — it is still 0 at that point
  // and useState's initializer only runs once. Re-sync until the operator edits the field
  // themselves, so a slow settings fetch doesn't leave the duration (and therefore the Suppress
  // button) permanently stuck at an invalid default.
  const isDurationDirtyRef = useRef(false);

  const handleDurationChange = useCallback((value: string) => {
    isDurationDirtyRef.current = true;
    setDurationValue(value);
  }, []);

  useEffect(() => {
    if (!isDurationDirtyRef.current) {
      setDurationValue(String(defaultDurationSec));
    }
  }, [defaultDurationSec]);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      const durationSec = parseInt(durationValue, 10);
      if (durationSec > 0 && durationSec <= maxSuppressionTime) {
        onSuppressGroup(group, durationSec, isPriority ? HIGH_PRIORITY_SUPPRESSION : undefined);
      }
    },
    [durationValue, group, isPriority, maxSuppressionTime, onSuppressGroup],
  );

  const lastSeen = new Date(group.lastSeenAt);
  const firstSeen = new Date(group.firstSeenAt);
  const windowDuration = lastSeen.getTime() - firstSeen.getTime();
  const offsetHz = parseKvNumber(group.offset);
  const signalLevelValue = parseKvNumber(group.signalLevel);
  const snrValue = parseKvNumber(group.snr);
  const patternType = typeof group.patternType === 'string' ? group.patternType : '-';

  return (
    <TableRow key={`${group.id}-${group.createdAt}`}>
      <TableCell className="px-2 py-2 text-center">
        {renderSignalDirectionIcon(
          group.direction,
          intl.formatMessage({
            id: `detections.table.directions.${group.direction}`,
          }),
        )}
      </TableCell>
      <TableCell className="px-2 py-2 text-center">
        {renderSignalTypeIcon(
          group.signalType as SignalType,
          intl.formatMessage({ id: `signalType.${group.signalType}` }),
        )}
      </TableCell>
      <TableCell className="px-2 py-2">
        <Badge variant={getStatusVariant(group.status)}>
          {intl.formatMessage({ id: getStatusTranslationKey(group.status) })}
        </Badge>
      </TableCell>
      <TableCell className="px-2 py-2 font-mono">
        {formatOptionalNumber(hzToMhz(group.frequency))}
      </TableCell>
      <TableCell className="px-2 py-2 font-mono">
        {offsetHz ? formatOptionalNumber(hzToMhz(offsetHz)) : null}
      </TableCell>
      <TableCell className="px-2 py-2">
        {signalLevelValue ? formatOptionalNumber(signalLevelValue) : null}
      </TableCell>
      <TableCell className="px-2 py-2">{formatOptionalNumber(snrValue)}</TableCell>
      <TableCell className="px-2 py-2">{patternType}</TableCell>
      <TableCell className="px-2 py-2">
        <FormattedDate
          value={lastSeen}
          year="numeric"
          month="numeric"
          day="numeric"
          hour="2-digit"
          minute="2-digit"
          second="2-digit"
        />
      </TableCell>
      <TableCell className="px-2 py-2 text-xs">
        {windowDuration > 0 && (
          <div>
            {intl.formatMessage({ id: 'detections.table.window' })}:{' '}
            <FormattedDate value={firstSeen} hour="2-digit" minute="2-digit" second="2-digit" />
            {' - '}
            <FormattedDate value={lastSeen} hour="2-digit" minute="2-digit" second="2-digit" /> (
            {formatDuration(windowDuration)})
          </div>
        )}
      </TableCell>
      <TableCell className="px-2 py-2 tabular-nums">{group.detectionsCount ?? 0}</TableCell>
      <TableCell className="px-2 py-2">
        {canManageSuppressions && group.status === DetectionStatus.DETECTED ? (
          <form className="flex items-center gap-2" onSubmit={handleSubmit}>
            <label className="flex items-center gap-1 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isPriority}
                onChange={(e) => setIsPriority(e.target.checked)}
                className="cursor-pointer"
              />
              {intl.formatMessage({ id: 'detections.table.priority' })}
            </label>
            <SuppressDetectionGroupAction
              durationValue={durationValue}
              disabled={isLoading}
              durationLabel={intl.formatMessage({
                id: 'detections.table.suppressionDuration',
              })}
              buttonLabel={intl.formatMessage({
                id: 'detections.related.suppressOnMain',
              })}
              maxDuration={maxSuppressionTime}
              onDurationChange={handleDurationChange}
            />
          </form>
        ) : (
          '-'
        )}
      </TableCell>
    </TableRow>
  );
});

type LinkedDeviceGroupsCompactTableProps = {
  canManageSuppressions: boolean;
  defaultDurationSec: number;
  deviceId: string;
  deviceSerialNumber: string;
  maxSuppressionTime: number;
  onPageChange: (page: number) => void;
  onSuppressGroup: (group: DetectionGroup, durationSec: number, priority?: number) => void;
  page: number;
  pageSize?: number;
};

export function LinkedDeviceGroupsCompactTable({
  canManageSuppressions,
  defaultDurationSec,
  deviceId,
  deviceSerialNumber,
  maxSuppressionTime,
  onPageChange,
  onSuppressGroup,
  page,
  pageSize = DEFAULT_PAGE_SIZE,
}: LinkedDeviceGroupsCompactTableProps) {
  const intl = useIntl();
  const { data, isLoading, error, refetch } = useDetectionGroups(
    { deviceId, page, pageSize, status: DetectionStatus.DETECTED },
    {
      enabled: Boolean(deviceId),
      refetchInterval: 30_000,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
    },
  );

  const groups = data?.groups ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handlePrevPage = useCallback(
    () => onPageChange(Math.max(1, page - 1)),
    [onPageChange, page],
  );

  const handleNextPage = useCallback(
    () => onPageChange(Math.min(totalPages, page + 1)),
    [onPageChange, page, totalPages],
  );

  return (
    <div
      className="rounded-lg border border-border bg-background-light p-3"
      data-testid="linked-device-compact-table"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">
          {intl.formatMessage(
            { id: 'detections.related.compactTitle' },
            { serial: deviceSerialNumber },
          )}
        </h3>
      </div>

      <Table className="text-xs">
        <TableHeader>
          <TableRow>
            <TableHead className="h-8 px-2 text-center">
              {intl.formatMessage({ id: 'detections.table.direction' })}
            </TableHead>
            <TableHead className="h-8 px-2 text-center">
              {intl.formatMessage({ id: 'detections.table.type' })}
            </TableHead>
            <TableHead className="h-8 px-2">
              {intl.formatMessage({ id: 'detections.table.status' })}
            </TableHead>
            <TableHead className="h-8 px-2">
              {intl.formatMessage({ id: 'detections.table.frequency' })}
            </TableHead>
            <TableHead className="h-8 px-2">
              {intl.formatMessage({ id: 'detections.table.offset' })}
            </TableHead>
            <TableHead className="h-8 px-2">
              {intl.formatMessage({ id: 'detections.table.signalLevel' })}
            </TableHead>
            <TableHead className="h-8 px-2">
              {intl.formatMessage({ id: 'detections.table.snr' })}
            </TableHead>
            <TableHead className="h-8 px-2">
              {intl.formatMessage({ id: 'detections.table.pattern' })}
            </TableHead>
            <TableHead className="h-8 px-2">
              {intl.formatMessage({ id: 'detections.table.lastSeen' })}
            </TableHead>
            <TableHead className="h-8 px-2">
              {intl.formatMessage({ id: 'detections.table.details' })}
            </TableHead>
            <TableHead className="h-8 px-2 tabular-nums">
              {intl.formatMessage({ id: 'detections.table.count' })}
            </TableHead>
            <TableHead className="h-8 px-2">
              {intl.formatMessage({ id: 'detections.table.actions' })}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={COL_SPAN} className="px-2 py-3 text-center">
                {intl.formatMessage({ id: 'detections.related.loading' })}
              </TableCell>
            </TableRow>
          )}

          {error && (
            <TableRow>
              <TableCell colSpan={COL_SPAN} className="px-2 py-3 text-center text-destructive">
                <div className="flex items-center justify-center gap-2">
                  <span>{intl.formatMessage({ id: 'detections.related.failedToLoad' })}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => refetch()}
                  >
                    {intl.formatMessage({ id: 'common.retry' })}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          )}

          {!isLoading && !error && groups.length === 0 && (
            <TableRow>
              <TableCell colSpan={COL_SPAN} className="px-2 py-3 text-center">
                {intl.formatMessage({ id: 'detections.related.noGroups' })}
              </TableCell>
            </TableRow>
          )}

          {!isLoading &&
            !error &&
            groups.map((group) => (
              <LinkedDetectionGroupRow
                key={`${group.id}-${group.createdAt}`}
                canManageSuppressions={canManageSuppressions}
                defaultDurationSec={defaultDurationSec}
                group={group}
                isLoading={isLoading}
                maxSuppressionTime={maxSuppressionTime}
                onSuppressGroup={onSuppressGroup}
              />
            ))}
        </TableBody>
      </Table>

      <div className="mt-3 flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={page <= 1 || isLoading}
          onClick={handlePrevPage}
        >
          {intl.formatMessage({ id: 'common.previous' })}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={page >= totalPages || isLoading}
          onClick={handleNextPage}
        >
          {intl.formatMessage({ id: 'common.next' })}
        </Button>
      </div>
    </div>
  );
}
